import { Injectable } from '@angular/core';
import { fileTypeFromBuffer } from 'file-type';
import {
  AllowedExtensions,
  AllowedMimeTypes,
  AllowedMimeTypesList,
  FileSizeLimit,
  FileValidationErrors,
} from '../../enums/file-validation';

@Injectable({
  providedIn: 'root',
})
export class FileValidationService {
  validationErrors: string[] = [];

  private validateFileSize(file: File) {
    if (file.size > FileSizeLimit) {
      this.validationErrors.push(FileValidationErrors.InvalidFileSize);
    }
  }

  private validateFileName(file: File) {
    const baseName = file.name.includes('.')
      ? file.name.substring(0, file.name.lastIndexOf('.'))
      : file.name;

    // Allow: letters (a-z, A-Z), digits (0-9), spaces, hyphens, underscores, dots
    const safeFileNamePattern = /^[a-zA-Z0-9 .\-_]+$/;
    if (!safeFileNamePattern.test(baseName)) {
      this.validationErrors.push(FileValidationErrors.InvalidFileName);
    }
  }

  private validateFileExtension(file: File) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !AllowedExtensions.includes(fileExtension)) {
      const error = FileValidationErrors.InvalidFileExtension.replace(
        '##AllowedTypes##',
        AllowedExtensions.join(', '),
      );
      this.validationErrors.push(error);
    }
  }

  private validateMimeType(file: File) {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !AllowedMimeTypes[extension]) {
      this.validationErrors.push(FileValidationErrors.InvalidMIMEType);
      return;
    }

    const uploadedFileMime = file.type.toLowerCase();

    if (!uploadedFileMime) {
      this.validationErrors.push(FileValidationErrors.InvalidMIMEType);
      return;
    }

    if (!AllowedMimeTypes[extension].includes(uploadedFileMime)) {
      this.validationErrors.push(FileValidationErrors.InvalidMIMEType);
    }
  }

  private async validateMagicBytes(file: File) {
    const buffer = await file.arrayBuffer();
    const uploadedFile = await fileTypeFromBuffer(buffer);
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    const skipMagicBytesFor = ['txt', 'csv'];
    if (fileExtension && skipMagicBytesFor.includes(fileExtension)) return;

    if (!uploadedFile) {
      this.validationErrors.push(FileValidationErrors.InvalidMagicBytes);
      return;
    }

    if (!AllowedMimeTypesList.includes(uploadedFile.mime)) {
      this.validationErrors.push(FileValidationErrors.InvalidMagicBytes);
      return;
    }

    if (
      fileExtension &&
      AllowedMimeTypes[fileExtension] &&
      !AllowedMimeTypes[fileExtension].includes(uploadedFile.mime)
    ) {
      this.validationErrors.push(FileValidationErrors.InvalidMagicBytes);
    }
  }

  async validateFile(file: File): Promise<boolean> {
    this.validationErrors = [];

    this.validateFileName(file);
    this.validateFileSize(file);
    this.validateFileExtension(file);
    this.validateMimeType(file);
    await this.validateMagicBytes(file);
    return this.validationErrors.length === 0;
  }
}
